/**
 * MultiFieldOutlinerView.tsx - Multi-Column Outliner with Adjustable Field Widths
 * 
 * CHANGE HISTORY:
 * - 251120_2118 danielbeer: Created multi-field outliner with Tab navigation and resizable columns
 * 
 * Features:
 * - Multiple fields per row (columns)
 * - Adjustable column widths via drag handles
 * - Tab/Shift+Tab navigation between fields
 * - All existing OutlinerView functionality preserved
 * - Field configuration (add/remove/reorder columns)
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { OutlinerView, OutlinerNode, OutlinerViewProps } from './OutlinerView';
import '../styles/multi-field-outliner.css';

export interface FieldDefinition {
  id: string;
  label: string;
  width: number;  // in pixels
  type: 'text' | 'number' | 'date' | 'select';
  editable: boolean;
  resizable: boolean;
  options?: string[];  // For 'select' type
  placeholder?: string;
}

export interface MultiFieldNode extends OutlinerNode {
  fields: {
    [fieldId: string]: string;
  };
}

export interface MultiFieldOutlinerViewProps extends Omit<OutlinerViewProps, 'nodes'> {
  nodes: MultiFieldNode[];
  fieldDefinitions: FieldDefinition[];
  onFieldDefinitionsChange?: (definitions: FieldDefinition[]) => void;
  showHeader?: boolean;
}

export const MultiFieldOutlinerView: React.FC<MultiFieldOutlinerViewProps> = ({
  nodes,
  fieldDefinitions: initialFieldDefinitions,
  onFieldDefinitionsChange,
  showHeader = true,
  onChange,
  ...otherProps
}) => {
  const [fieldDefinitions, setFieldDefinitions] = useState(initialFieldDefinitions);
  const [editingField, setEditingField] = useState<{ nodeId: string; fieldId: string } | null>(null);
  const [resizingField, setResizingField] = useState<{ fieldId: string; startX: number; startWidth: number } | null>(null);
  
  // Refs for field inputs to manage focus
  const fieldInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  
  // Update field definitions when prop changes
  useEffect(() => {
    setFieldDefinitions(initialFieldDefinitions);
  }, [initialFieldDefinitions]);
  
  // Handle field definition changes
  const updateFieldDefinitions = useCallback((newDefinitions: FieldDefinition[]) => {
    setFieldDefinitions(newDefinitions);
    onFieldDefinitionsChange?.(newDefinitions);
  }, [onFieldDefinitionsChange]);
  
  // Handle field width resize
  const handleResizeStart = useCallback((e: React.MouseEvent, fieldId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    const field = fieldDefinitions.find(f => f.id === fieldId);
    if (!field) return;
    
    setResizingField({
      fieldId,
      startX: e.clientX,
      startWidth: field.width
    });
  }, [fieldDefinitions]);
  
  // Handle mouse move during resize
  useEffect(() => {
    if (!resizingField) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizingField.startX;
      const newWidth = Math.max(50, resizingField.startWidth + delta); // Min width 50px
      
      setFieldDefinitions(prev => 
        prev.map(f => 
          f.id === resizingField.fieldId 
            ? { ...f, width: newWidth }
            : f
        )
      );
    };
    
    const handleMouseUp = () => {
      if (resizingField) {
        // Notify parent of final width
        onFieldDefinitionsChange?.(fieldDefinitions);
      }
      setResizingField(null);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingField, fieldDefinitions, onFieldDefinitionsChange]);
  
  // Handle field value change
  const handleFieldChange = useCallback((nodeId: string, fieldId: string, value: string) => {
    const updatedNodes = nodes.map(node => {
      if (node.id === nodeId) {
        return {
          ...node,
          fields: {
            ...node.fields,
            [fieldId]: value
          }
        };
      }
      return node;
    });
    
    onChange?.(updatedNodes);
  }, [nodes, onChange]);
  
  // Handle Tab/Shift+Tab navigation between fields
  const handleFieldKeyDown = useCallback((
    e: React.KeyboardEvent<HTMLInputElement>,
    nodeId: string,
    currentFieldIndex: number
  ) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      
      const editableFields = fieldDefinitions.filter(f => f.editable);
      const currentEditableIndex = editableFields.findIndex(f => f.id === fieldDefinitions[currentFieldIndex].id);
      
      if (e.shiftKey) {
        // Shift+Tab: Move to previous field
        if (currentEditableIndex > 0) {
          const prevField = editableFields[currentEditableIndex - 1];
          setEditingField({ nodeId, fieldId: prevField.id });
          
          // Focus the input after state update
          setTimeout(() => {
            const inputKey = `${nodeId}-${prevField.id}`;
            const input = fieldInputRefs.current.get(inputKey);
            input?.focus();
          }, 0);
        } else {
          // Move to last field of previous node
          const nodeIndex = nodes.findIndex(n => n.id === nodeId);
          if (nodeIndex > 0) {
            const prevNode = nodes[nodeIndex - 1];
            const lastField = editableFields[editableFields.length - 1];
            setEditingField({ nodeId: prevNode.id, fieldId: lastField.id });
            
            setTimeout(() => {
              const inputKey = `${prevNode.id}-${lastField.id}`;
              const input = fieldInputRefs.current.get(inputKey);
              input?.focus();
            }, 0);
          }
        }
      } else {
        // Tab: Move to next field
        if (currentEditableIndex < editableFields.length - 1) {
          const nextField = editableFields[currentEditableIndex + 1];
          setEditingField({ nodeId, fieldId: nextField.id });
          
          setTimeout(() => {
            const inputKey = `${nodeId}-${nextField.id}`;
            const input = fieldInputRefs.current.get(inputKey);
            input?.focus();
          }, 0);
        } else {
          // Move to first field of next node
          const nodeIndex = nodes.findIndex(n => n.id === nodeId);
          if (nodeIndex < nodes.length - 1) {
            const nextNode = nodes[nodeIndex + 1];
            const firstField = editableFields[0];
            setEditingField({ nodeId: nextNode.id, fieldId: firstField.id });
            
            setTimeout(() => {
              const inputKey = `${nextNode.id}-${firstField.id}`;
              const input = fieldInputRefs.current.get(inputKey);
              input?.focus();
            }, 0);
          }
        }
      }
    } else if (e.key === 'Enter') {
      // Enter: Exit edit mode
      e.preventDefault();
      setEditingField(null);
    } else if (e.key === 'Escape') {
      // Escape: Cancel edit
      e.preventDefault();
      setEditingField(null);
    }
  }, [fieldDefinitions, nodes]);
  
  // Render field cell
  const renderFieldCell = useCallback((node: MultiFieldNode, field: FieldDefinition, fieldIndex: number) => {
    const isEditing = editingField?.nodeId === node.id && editingField?.fieldId === field.id;
    const value = node.fields[field.id] || '';
    const inputKey = `${node.id}-${field.id}`;
    
    return (
      <div
        key={field.id}
        className="outliner-field-cell"
        style={{ width: `${field.width}px` }}
        onClick={(e) => {
          e.stopPropagation();
          if (field.editable) {
            setEditingField({ nodeId: node.id, fieldId: field.id });
          }
        }}
      >
        {isEditing ? (
          field.type === 'select' ? (
            <select
              ref={(el) => {
                if (el) fieldInputRefs.current.set(inputKey, el as any);
              }}
              value={value}
              onChange={(e) => handleFieldChange(node.id, field.id, e.target.value)}
              onBlur={() => setEditingField(null)}
              onKeyDown={(e: any) => handleFieldKeyDown(e, node.id, fieldIndex)}
              autoFocus
              className="field-input"
            >
              <option value="">Select...</option>
              {field.options?.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          ) : (
            <input
              ref={(el) => {
                if (el) fieldInputRefs.current.set(inputKey, el);
              }}
              type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
              value={value}
              onChange={(e) => handleFieldChange(node.id, field.id, e.target.value)}
              onBlur={() => setEditingField(null)}
              onKeyDown={(e) => handleFieldKeyDown(e, node.id, fieldIndex)}
              placeholder={field.placeholder}
              autoFocus
              className="field-input"
            />
          )
        ) : (
          <span className="field-value" title={value}>
            {value || (field.placeholder ? <span className="field-placeholder">{field.placeholder}</span> : '')}
          </span>
        )}
        
        {field.resizable && (
          <div
            className="field-resize-handle"
            onMouseDown={(e) => handleResizeStart(e, field.id)}
          />
        )}
      </div>
    );
  }, [editingField, handleFieldChange, handleFieldKeyDown, handleResizeStart]);
  
  // Render header row
  const renderHeader = useCallback(() => {
    if (!showHeader) return null;
    
    return (
      <div className="outliner-header-row">
        <div className="outliner-header-indent" />
        <div className="outliner-header-expand" />
        {fieldDefinitions.map(field => (
          <div
            key={field.id}
            className="outliner-header-cell"
            style={{ width: `${field.width}px` }}
          >
            <span className="header-label">{field.label}</span>
            {field.resizable && (
              <div
                className="field-resize-handle"
                onMouseDown={(e) => handleResizeStart(e, field.id)}
              />
            )}
          </div>
        ))}
      </div>
    );
  }, [showHeader, fieldDefinitions, handleResizeStart]);
  
  // Custom node renderer that adds fields
  const renderMultiFieldNode = useCallback((node: MultiFieldNode, depth: number) => {
    return (
      <div key={node.id} className="multi-field-outliner-row">
        {/* Indent */}
        <div className="row-indent" style={{ width: `${depth * 20}px` }} />
        
        {/* Expand button placeholder (handled by base OutlinerView) */}
        <div className="row-expand-placeholder" />
        
        {/* Fields */}
        {fieldDefinitions.map((field, index) => renderFieldCell(node, field, index))}
      </div>
    );
  }, [fieldDefinitions, renderFieldCell]);
  
  // Custom node content renderer for multi-field
  const renderMultiFieldContent = useCallback((node: OutlinerNode, isEditing: boolean, updateNodeFn: any) => {
    const multiFieldNode = node as MultiFieldNode;
    return (
      <div className="multi-field-content">
        {fieldDefinitions.map((field, index) => {
          const isEditingThisField = editingField?.nodeId === multiFieldNode.id && editingField?.fieldId === field.id;
          const value = multiFieldNode.fields?.[field.id] || '';
          const inputKey = `${multiFieldNode.id}-${field.id}`;
          
          return (
            <div
              key={field.id}
              className="outliner-field-cell"
              style={{ width: `${field.width}px` }}
              onClick={(e) => {
                e.stopPropagation();
                if (field.editable) {
                  setEditingField({ nodeId: multiFieldNode.id, fieldId: field.id });
                }
              }}
            >
              {isEditingThisField ? (
                field.type === 'select' ? (
                  <select
                    ref={(el) => {
                      if (el) fieldInputRefs.current.set(inputKey, el as any);
                    }}
                    value={value}
                    onChange={(e) => handleFieldChange(multiFieldNode.id, field.id, e.target.value)}
                    onBlur={() => setEditingField(null)}
                    onKeyDown={(e: any) => handleFieldKeyDown(e, multiFieldNode.id, index)}
                    autoFocus
                    className="field-input"
                  >
                    <option value="">Select...</option>
                    {field.options?.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    ref={(el) => {
                      if (el) fieldInputRefs.current.set(inputKey, el);
                    }}
                    type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                    value={value}
                    onChange={(e) => handleFieldChange(multiFieldNode.id, field.id, e.target.value)}
                    onBlur={() => setEditingField(null)}
                    onKeyDown={(e) => handleFieldKeyDown(e, multiFieldNode.id, index)}
                    placeholder={field.placeholder}
                    autoFocus
                    className="field-input"
                  />
                )
              ) : (
                <span className="field-value" title={value}>
                  {value || (field.placeholder ? <span className="field-placeholder">{field.placeholder}</span> : '')}
                </span>
              )}
              
              {field.resizable && (
                <div
                  className="field-resize-handle"
                  onMouseDown={(e) => handleResizeStart(e, field.id)}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }, [fieldDefinitions, editingField, handleFieldChange, handleFieldKeyDown, handleResizeStart]);

  return (
    <div className="multi-field-outliner-container">
      {renderHeader()}
      
      {/* Use base OutlinerView with custom node content renderer */}
      <OutlinerView
        {...otherProps}
        nodes={nodes}
        onChange={onChange}
        className="multi-field-outliner-base"
        renderNodeContent={renderMultiFieldContent}
      />
    </div>
  );
};
